import React, { useEffect, useRef, useState } from "react";
import { createSpecifiation, editSpecification, getContracts } from "@/service/weexpertService";
import { useTranslation } from "react-i18next";
import { Search } from "../../components/ui/combobox-search";
import { InputWithAlert } from "../../components/ui/input-with-alert";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { TrashIcon, GripVertical, Plus } from "lucide-react";
import { v4 as uuidv4 } from 'uuid';
import { format } from "date-fns";
import { DatePicker } from "../../components/ui/date-picker";
import { AlertCircle } from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { SelectWithAlert } from "../ui/select-with-alert";
import { CONTRACT_STATE, CONTRACT_TYPE, Signatories, parseNumber } from "@/constants";
import { toast } from "../ui/use-toast";
import { useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


const SpecificationPosition = ({ id, text, grossAmount, onDelete, onChange, endDate, order }) => {
  const { t } = useTranslation()
  const [currentText, setText] = useState(text);
  const [currentValue, setValue] = useState(+grossAmount || undefined);
  const [dateOfEnd, setDateOfEnd] = useState(endDate || undefined);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      setDateOfEnd(formattedDate);
      onChange({ id, text: currentText, grossAmount: currentValue, endDate: formattedDate });
    } else {
      setDateOfEnd(undefined);
      onChange({ id, text: currentText, grossAmount: currentValue, endDate: undefined });
    }
  };

  useEffect(() => {
    onChange({ id, text: currentText, grossAmount: currentValue, endDate: dateOfEnd });
  }, [currentText, currentValue, dateOfEnd]);


  return (
    <div ref={setNodeRef} style={style} key={id} className="w-full flex-wrap md:flex-nowrap flex justify-between mb-4 ">
      <div className="w-auto pl-4 flex items-start">
        <button
          className="touch-none cursor-grab hover:bg-gray-100 rounded p-1 mr-2 mt-2"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5 text-gray-400" />
        </button>
        <InputWithAlert
          type="number"
          value={order}
          disabled={true}
          inputClassName={'h-10 w-[70px] mr-4 p-2 bg-gray-100 text-center mb-2'}
        />
      </div>
      <div className="w-full md:w-10/12">
        <Textarea
          placeholder={t("specifications:spec_text")}
          value={currentText}
          onChange={(e) => setText(e.target.value)}
          className={'h-[200px] bg-white'}
        />
      </div>
      <div className="w-full md:w-2/12 md:pl-4 flex md:flex-col justify-between md:justify-start items-center flex-wrap md:flex-nowrap pt-2 md:pt-0">
        <div className="w-[300px] md:w-full ">
          <InputWithAlert
            label={t("bills:gross_amount")}
            placeholder={t("bills:gross_amount")}
            value={currentValue}
            onChange={(e) => setValue(parseNumber(e.target.value))}
            inputClassName={'h-10 w-full bg-white mb-4'}
          />
        </div>
        <div className="w-[300px] md:w-full ">
          <DatePicker
            label={t("specifications:spec_end_date")}
            date={dateOfEnd ? new Date(dateOfEnd) : null}
            defaultMonth={dateOfEnd ? new Date(dateOfEnd) : new Date()}
            setDate={handleDateChange}
            inputClassName={'h-10 min-w-[110px] text-base mb-4 md:mb-6'}
          />
        </div>
        <div className="pt-2 md:pt-0 w-2/12 w-full flex justify-end">
          <Button className="h-8 " onClick={() => onDelete(id)}>
            <TrashIcon className="w-[16px] h-6" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SpecificationForm = ({ onSaved, initialData = null, action }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const saveTimeoutRef = useRef(null);

  const [contract, setContract] = useState(initialData ? initialData.contract : undefined);
  const [changes, setChanges] = useState(initialData ? initialData.specificationItems.map(item => ({
    ...item,
    text: item.description || item.text || '',
    order: item.order || 1
  })) : []);
  const [descriptionComment, setDescriptionComment] = useState(initialData ? initialData.description : undefined);
  const [functionalityComment, setFunctionalityComment] = useState(initialData ? initialData.functionality : undefined);
  const [signatory, setSignatory] = useState(initialData ? initialData.signatory : '');
  const [inputChecked, setInputChecked] = useState(false);

  const prevStateRef = useRef({
    contract: null,
    descriptionComment: null,
    functionalityComment: null,
    signatory: null,
    changes: []
  });
  
  useEffect(() => {
    setInputChecked(true);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
  
    const isSameState = (
      prevStateRef.current.contract === contract &&
      prevStateRef.current.descriptionComment === descriptionComment &&
      prevStateRef.current.functionalityComment === functionalityComment &&
      prevStateRef.current.signatory === signatory &&
      JSON.stringify(prevStateRef.current.changes) === JSON.stringify(changes)
    );
  
    const hasValidChanges = !initialData && (
      contract &&
      descriptionComment &&
      functionalityComment &&
      signatory &&
      changes.length > 0 &&
      changes.every(item => item.text && item.grossAmount > 0 && item.endDate)
    );
  
    const hasChanges = (
      initialData && (
        contract !== initialData.contract ||
        descriptionComment !== initialData.description ||
        functionalityComment !== initialData.functionality ||
        signatory !== initialData.signatory ||
        changes.some((item) => {
          const initialItem = initialData.specificationItems.find(e => e.id === item.id);
          if (!initialItem) {
            return item.text && item.grossAmount > 0 && item.endDate;
          }
          return (
            item.text !== initialItem.description ||
            item.grossAmount !== initialItem.grossAmount ||
            item.endDate !== initialItem.endDate
          );
        }) ||
        initialData.specificationItems.some((initialItem) =>
          !changes.some((item) => item.id === initialItem.id)
        ) ||
        changes.length !== initialData.specificationItems.length
      )
    );
  
    if (changes.some(item => !item.text || !item.grossAmount || !item.endDate)) {
      return;
    }
  
    if ((hasValidChanges || hasChanges) && !isSameState) {
      saveTimeoutRef.current = setTimeout(() => {
        prevStateRef.current = {
          contract,
          descriptionComment,
          functionalityComment,
          signatory,
          changes
        };
        onSaved(
          contract,
          descriptionComment,
          functionalityComment,
          signatory,
          changes,
          true
        );
        setInputChecked(false);
      }, 3000);
    }
  }, [action, contract, descriptionComment, functionalityComment, changes, signatory, onSaved, inputChecked, initialData]);



  const onDelete = (id) => {
    setChanges((prev) => prev.filter(e => e.id !== id));
  };

  const handleChange = ({ id, text, grossAmount, endDate }) => {
    setChanges((prev) => {
      const index = prev.findIndex(el => el.id === id);
      const newChanges = [...prev];
      newChanges[index] = { ...newChanges[index], text, grossAmount, endDate };
      return newChanges;
    });
  };


  const addNewPosition = () => {
    const newPositionNumber = changes.length > 0 ? Math.max(...changes.map(e => e.order)) + 1 : 1;
    setChanges((prev) => [
      ...prev,
      { id: uuidv4(), text: '', grossAmount: 0, endDate: null, order: newPositionNumber }
    ]);
  };

  const insertNewPositionAfter = (afterIndex) => {
    setChanges((prev) => {
      const newId = uuidv4();
      const newItem = { id: newId, text: '', grossAmount: 0, endDate: null, order: afterIndex + 2 };
      
      // Insert the new item at the correct position
      const newChanges = [...prev];
      newChanges.splice(afterIndex + 1, 0, newItem);
      
      // Recalculate order values for all items
      return newChanges.map((item, index) => ({
        ...item,
        order: index + 1
      }));
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setChanges((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update the order values based on new positions
        return newItems.map((item, index) => ({
          ...item,
          order: index + 1
        }));
      });
    }
  };

  return (
    <div className="relative bg-grayLightMainBg px-8">
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="flex justify-between py-8 items-center">
          <div className="w-5/12 text-2xl md:text-4xl">{t("specifications:spec")}</div>
          <div>
            <Button className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
              onClick={() => {
                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current); 
                }
                setInputChecked(true)
                onSaved(
                  contract,
                  descriptionComment,
                  functionalityComment,
                  signatory,
                  changes,
                  false
                )
              }}>
              {t("common:button_save")}
            </Button>
          </div>
        </div>
      </div>
      <div className='w-full md:w-9/12 lg:w-8/12 xl:w-7/12 py-8'>
        <Search
          fetchOption={(query) => {
            return getContracts(100, 0, query, [{ field: 'contractType', value: [CONTRACT_TYPE.UMOWA_O_DZIELO] }, { field: 'contractStatus', value: [CONTRACT_STATE.Accepted] }])
              .then((res) => {
                return res.data.contracts.map(contract => {
                  return {
                    label: contract.contractId + ` (${contract.client.firstName} ${contract.client.lastName})`,
                    value: contract.id,
                    contract: contract
                  };
                });
              });
          }}
          placeholder={t('contracts:contract')}
          label={t('contracts:contract')}
          value={contract ? contract.contractId : null}
          error={inputChecked && (!contract || (contract && contract.specifications?.length > 0 && action === 'creation'))}
          errorMessage={
            inputChecked && !contract ?
              t('empty_data_invalid') :
              t('alerts:error_contract_create')
          }
          onChange={(e) => setContract(e.contract)}
        />
        {
          contract && (<SelectWithAlert
            label={t("bills:bills_signatory")}
            options={Signatories[contract.representativeCompany]}
            value={signatory}
            onChangeValue={setSignatory}
            inputClassName={'h-12 mt-2 mb-4 bg-white'}
          />)
        }
        <div className='text-sm font-medium pt-4 pb-6'>
          {t('specifications:spec_description')}
        </div>
        <Textarea
          placeholder={t('specifications:spec_description')}
          value={descriptionComment}
          onChange={(e) => setDescriptionComment(e.target.value)}
          error={inputChecked && !descriptionComment}
          errorMessage={t('empty_data_invalid')}
          className="h-20 bg-white"
        />
        <div className='text-sm font-medium pt-4 pb-6'>
          {t('specifications:spec_functionality')}
        </div>
        <Textarea
          placeholder={t('specifications:spec_functionality')}
          value={functionalityComment}
          onChange={(e) => setFunctionalityComment(e.target.value)}
          error={inputChecked && !functionalityComment}
          errorMessage={t('empty_data_invalid')}
          className="h-20 bg-white"
        />
        <div className='w-full pt-8'>
          <InputWithAlert
            label={t('bills:gross_amount')}
            value={changes.reduce((acc, e) => (acc + (e.grossAmount ? +e.grossAmount : 0)), 0).toFixed(2)}
            disabled={true}
            inputClassName={'h-12  mb-4 bg-white'}
          />

        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={changes.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className='w-full '>
            {changes.map((e, index) => (
              <React.Fragment key={e.id}>
                <SpecificationPosition
                  id={e.id}
                  text={e.description}
                  grossAmount={e.grossAmount}
                  endDate={e.endDate}
                  onDelete={onDelete}
                  onChange={handleChange}
                  order={e.order}
                />
                {index < changes.length - 1 && (
                  <div 
                    className="relative h-8 group cursor-pointer"
                  >
                    <div className="absolute inset-x-0 top-1/2 h-[1px] bg-gray-200 group-hover:bg-gray-400 transition-colors" />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        type="button"
                        className="bg-white border border-gray-300 rounded-full p-1.5 hover:bg-gray-50 shadow-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          insertNewPositionAfter(index);
                        }}
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="w-full items-center pt-2 pb-16">
        <Button className="w-full md:w-2/12 h-12" variant="outline" onClick={addNewPosition}>
          {t("button_add")}
        </Button>
        {(inputChecked && changes.length < 1) && (
          <div className="mt-4 w-full md:w-2/12">
            <Alert variant="destructive" className="relative mb-2 h-12 flex items-center justify-start !pl-[0.75rem]">
              <AlertCircle className="h-4 w-4 !static !mr-2" />
              <AlertTitle className="m-0 !p-0">
                {t('empty_data_invalid')}
              </AlertTitle>
            </Alert>
          </div>
        )}
      </div>
    </div>
  );
};
